import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { Home } from "@/pages/Home";
import { ZelteListe } from "@/pages/ZelteListe";
import { ZeltSeite } from "@/pages/ZeltSeite";
import { VergleichSeite } from "@/pages/VergleichSeite";
import { GuidesListe } from "@/pages/GuidesListe";
import { GuideSeite } from "@/pages/GuideSeite";
import { KategorieSeite } from "@/pages/KategorieSeite";
import { FinderSeite } from "@/pages/FinderSeite";
import { ImpressumSeite } from "@/pages/ImpressumSeite";
import { DatenschutzSeite } from "@/pages/DatenschutzSeite";
import { AGBSeite } from "@/pages/AGBSeite";
import { VergleichBar } from "@/components/VergleichBar";
import { useRouter, matchRoute } from "@/hooks/useRouter";
import { useVergleich } from "@/hooks/useVergleich";
import { useDarkMode } from "@/hooks/useDarkMode";
import type { ZeltKategorie } from "@/data/zelte";
import type { GuideKategorie } from "@/data/guides";
import "./index.css";

// All category landing page slugs
const kategorieRoutes = [
  "kuppelzelte",
  "tunnelzelte",
  "familienzelte",
  "trekkingzelte",
  "geodaet-zelte",
  "tipi-zelte",
  "wurfzelte",
  "dachzelte",
  "winterzelte",
  "festivalzelte",
  "glamping-zelte",
  "partyzelte",
  "aufblasbare-zelte",
  "grosse-zelte",
  "guenstige-zelte",
  "3-personen-zelte",
  "6-personen-zelte",
  "2-personen-zelte",
  "4-personen-zelte",
  "campingzelte",
  "leichte-zelte",
];

export function App() {
  const { path, query, navigate } = useRouter();
  const { vergleichIds, toggleVergleich, removeFromVergleich, isInVergleich, count: vergleichCount, clearVergleich, replaceVergleich } = useVergleich();
  const { isDark, toggle: toggleTheme } = useDarkMode();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  // Render page based on route
  const renderPage = () => {
    // Home
    if (path === "/" || path === "") {
      return (
        <Home
          onNavigate={navigate}
          onToggleVergleich={toggleVergleich}
          vergleichIds={vergleichIds}
        />
      );
    }

    // Vergleich
    if (path === "/vergleich") {
      return (
        <VergleichSeite
          vergleichIds={vergleichIds}
          onRemove={removeFromVergleich}
          onClear={clearVergleich}
          onReplace={replaceVergleich}
          onNavigate={navigate}
        />
      );
    }

    // Zelt-Finder
    if (path === "/finder") {
      return (
        <FinderSeite
          onNavigate={navigate}
          onToggleVergleich={toggleVergleich}
          vergleichIds={vergleichIds}
        />
      );
    }

    // Zelt Detail
    const zeltMatch = matchRoute(path, "/zelte/:slug");
    if (zeltMatch.match && zeltMatch.params.slug) {
      return (
        <ZeltSeite
          slug={zeltMatch.params.slug}
          onNavigate={navigate}
          onToggleVergleich={toggleVergleich}
          isInVergleich={isInVergleich}
          vergleichIds={vergleichIds}
        />
      );
    }

    // Zelte Liste
    if (path === "/zelte" || path.startsWith("/zelte")) {
      const kategorie = query.get("kategorie") as ZeltKategorie | null;
      const suche = query.get("suche");
      return (
        <ZelteListe
          onNavigate={navigate}
          onToggleVergleich={toggleVergleich}
          vergleichIds={vergleichIds}
          initialKategorie={kategorie}
          initialSuche={suche}
        />
      );
    }

    // Guide Detail
    const guideMatch = matchRoute(path, "/ratgeber/:slug");
    if (guideMatch.match && guideMatch.params.slug) {
      return (
        <GuideSeite
          slug={guideMatch.params.slug}
          onNavigate={navigate}
        />
      );
    }

    // Guides Liste
    if (path === "/ratgeber" || path.startsWith("/ratgeber")) {
      const kategorie = query.get("kategorie") as GuideKategorie | null;
      const suche = query.get("suche");
      return (
        <GuidesListe
          onNavigate={navigate}
          initialKategorie={kategorie}
          initialSuche={suche}
        />
      );
    }

    // Category Landing Pages
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    if (kategorieRoutes.includes(cleanPath)) {
      return (
        <KategorieSeite
          slug={cleanPath}
          onNavigate={navigate}
          onToggleVergleich={toggleVergleich}
          vergleichIds={vergleichIds}
        />
      );
    }

    // Impressum
    if (path === "/impressum") {
      return <ImpressumSeite onNavigate={navigate} />;
    }

    // Datenschutz
    if (path === "/datenschutz") {
      return <DatenschutzSeite onNavigate={navigate} />;
    }

    // AGB
    if (path === "/agb") {
      return <AGBSeite onNavigate={navigate} />;
    }

    // 404
    return (
      <div className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <div className="text-8xl mb-6">🏕️</div>
          <h1 className="text-6xl font-bold mb-4 text-primary">404</h1>
          <h2 className="text-2xl font-semibold mb-3">Zelt nicht gefunden!</h2>
          <p className="text-muted-foreground mb-8">
            Ups! Diese Seite scheint sich im Wald verirrt zu haben.
            Vielleicht findest du dein Traumzelt auf unserer Startseite?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Zur Startseite
            </button>
            <button
              onClick={() => navigate("/zelte")}
              className="px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition-colors"
            >
              Alle Zelte ansehen
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-background">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:text-sm focus:font-medium">
          Zum Inhalt springen
        </a>
        <Navbar
          currentPath={path}
          onNavigate={navigate}
          vergleichCount={vergleichCount}
          isDark={isDark}
          onToggleTheme={toggleTheme}
        />
        <main id="main-content" className={vergleichCount > 0 && path !== "/vergleich" ? "flex-1 pb-24" : "flex-1"}>{renderPage()}</main>
        <Footer onNavigate={navigate} />
        <VergleichBar
          ids={vergleichIds}
          currentPath={path}
          onRemove={removeFromVergleich}
          onClear={clearVergleich}
          onNavigate={navigate}
        />
        <CookieConsent
          onNavigate={navigate}
          offsetForBar={vergleichCount > 0 && path !== "/vergleich"}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
